"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@papier/ui/components/ui/button"
import { Card } from "@papier/ui/components/ui/card"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@papier/ui/components/ui/dialog"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from "@papier/ui/components/ui/form"
import { Input } from "@papier/ui/components/ui/input"
import { cn } from "@papier/ui/lib/utils"
import type { Organization } from "better-auth/plugins/organization"
import { Refresh as Loader2 } from "@papier/ui/components/icons"
import { type ComponentProps, useContext, useMemo } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { AuthUIContext } from "../../lib/auth-ui-provider"
import { getLocalizedError } from "../../lib/utils"
import type { AuthLocalization } from "../../localization/auth-localization"
import type { SettingsCardClassNames } from "../settings/shared/settings-card"
import { OrganizationCellView } from "./organization-cell-view"

export interface DeleteOrganizationDialogProps
    extends ComponentProps<typeof Dialog> {
    classNames?: SettingsCardClassNames
    onSuccess?: () => Promise<void>
    localization?: AuthLocalization
    organization: Organization
    onOpenChange?: (open: boolean) => void
    onCloseAutoFocus?: ComponentProps<typeof DialogContent>["onCloseAutoFocus"]
}

export function DeleteOrganizationDialog({
    classNames,
    onSuccess,
    localization: localizationProp,
    onOpenChange,
    onCloseAutoFocus,
    organization,
    ...props
}: DeleteOrganizationDialogProps) {
    const {
        authClient,
        hooks: { useListOrganizations },
        localization: contextLocalization,
        toast
    } = useContext(AuthUIContext)

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const { data: orgs, refetch: refetchOrganizations } = useListOrganizations()

    const formSchema = z.object({
        slug: z
            .string()
            .min(1, { message: localization.SLUG_REQUIRED })
            .refine((val) => val === organization.slug, {
                message: localization.SLUG_DOES_NOT_MATCH
            })
    })

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            slug: ""
        }
    })

    const { isSubmitting } = form.formState

    const deleteOrganization = async () => {
        try {
            await authClient.organization.delete({
                organizationId: organization.id,
                fetchOptions: { throw: true }
            })

            await refetchOrganizations?.()

            // Try setting another org when deleted, else set to null
            const anotherOrganization = orgs
                ?.filter((org) => org.id !== organization.id)
                .at(0)
            try {
                await authClient.organization.setActive({
                    organizationId: anotherOrganization?.id || null,
                    fetchOptions: { throw: true }
                })
            } catch {
                toast({
                    variant: "error",
                    message:
                        "There was an issue setting your active organization"
                })
            }

            toast({
                variant: "success",
                message: localization.DELETE_ORGANIZATION_SUCCESS
            })
            onOpenChange?.(false)
            await onSuccess?.()
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })
        }
    }

    return (
        <Dialog onOpenChange={onOpenChange} {...props}>
            <DialogContent
                onCloseAutoFocus={onCloseAutoFocus}
                className={cn("sm:max-w-md", classNames?.dialog?.content)}
            >
                <DialogHeader className={classNames?.dialog?.header}>
                    <DialogTitle
                        className={cn("text-lg md:text-xl", classNames?.title)}
                    >
                        {localization?.DELETE_ORGANIZATION}
                    </DialogTitle>

                    <DialogDescription
                        className={cn(
                            "text-xs md:text-sm",
                            classNames?.description
                        )}
                    >
                        {localization?.DELETE_ORGANIZATION_DESCRIPTION}
                    </DialogDescription>
                </DialogHeader>

                <Card className={cn("my-2 flex-row p-4", classNames?.cell)}>
                    <OrganizationCellView
                        organization={organization}
                        localization={localization}
                    />
                </Card>

                <Form {...form}>
                    <form
                        onSubmit={(event) => void form.handleSubmit(deleteOrganization)(event)}
                        className="grid gap-6"
                    >
                        <FormField
                            control={form.control}
                            name="slug"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className={classNames?.label}>
                                        {
                                            localization?.DELETE_ORGANIZATION_INSTRUCTIONS
                                        }

                                        <span className="font-bold">
                                            {organization.slug}
                                        </span>
                                    </FormLabel>

                                    <FormControl>
                                        <Input
                                            placeholder={organization.slug}
                                            className={classNames?.input}
                                            autoComplete="off"
                                            {...field}
                                        />
                                    </FormControl>

                                    <FormMessage
                                        className={classNames?.error}
                                    />
                                </FormItem>
                            )}
                        />

                        <DialogFooter className={classNames?.dialog?.footer}>
                            <Button
                                type="button"
                                variant="outline"
                                className={cn(
                                    classNames?.button,
                                    classNames?.outlineButton
                                )}
                                onClick={() => onOpenChange?.(false)}
                            >
                                {localization.CANCEL}
                            </Button>

                            <Button
                                className={cn(
                                    classNames?.button,
                                    classNames?.destructiveButton
                                )}
                                disabled={isSubmitting}
                                variant="destructive"
                                type="submit"
                            >
                                {isSubmitting && (
                                    <Loader2 className="animate-spin" />
                                )}

                                {localization?.DELETE_ORGANIZATION}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
